import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiService from "../../utils/apiService";

// Async thunks for API calls
export const forgotPassword = createAsyncThunk(
  "passwordReset/forgotPassword",
  async (email, { rejectWithValue }) => {
    try {
      const response = await apiService.post("/auth/forgot-password", { email }, { requiresAuth: false });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to send password reset email");
    }
  }
);

export const resetPassword = createAsyncThunk(
  "passwordReset/resetPassword",
  async ({ token, newPassword }, { rejectWithValue }) => {
    try {
      const response = await apiService.post("/auth/reset-password", { token, newPassword }, { requiresAuth: false });
      return response.data;
    } catch (error) {
      // Pass validation details from API (e.g. { error: "VALIDATION_ERROR", details: [{ path, message }] })
      const apiData = error.data;
      if (apiData?.details && Array.isArray(apiData.details)) {
        return rejectWithValue({
          message: apiData.details.map((d) => d.message).join(" "),
          details: apiData.details,
        });
      }
      return rejectWithValue({
        message: error.message || "Failed to reset password",
        details: null,
      });
    }
  }
);

export const verifyEmail = createAsyncThunk(
  "passwordReset/verifyEmail",
  async (token, { rejectWithValue }) => {
    try {
      const response = await apiService.get(`/auth/verify-email/${token}`, { requiresAuth: false });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to verify email");
    }
  }
);

export const resendEmailVerification = createAsyncThunk(
  "passwordReset/resendEmailVerification",
  async (email, { rejectWithValue }) => {
    try {
      const response = await apiService.post("/auth/resend-verification", { email }, { requiresAuth: false });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to send verification email");
    }
  }
);

const initialState = {
  isLoading: false,
  error: null,
  message: null,
  isVerified: false,
};

const passwordResetSlice = createSlice({
  name: "passwordReset",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearMessage: (state) => {
      state.message = null;
    },
    clearAll: (state) => {
      state.error = null;
      state.message = null;
      state.isVerified = false;
    },
  },
  extraReducers: (builder) => {
    // Forgot Password
    builder
      .addCase(forgotPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.message = action.payload.message || "Password reset email sent successfully";
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Reset Password
    builder
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.message = action.payload.message || "Password reset successfully";
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Verify Email
    builder
      .addCase(verifyEmail.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(verifyEmail.fulfilled, (state, action) => {
        state.isLoading = false;
        state.message = action.payload.message || "Email verified successfully";
        state.isVerified = true;
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Resend Email Verification
    builder
      .addCase(resendEmailVerification.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(resendEmailVerification.fulfilled, (state, action) => {
        state.isLoading = false;
        state.message = action.payload.message || "Verification email sent successfully";
      })
      .addCase(resendEmailVerification.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearMessage, clearAll } = passwordResetSlice.actions;
export default passwordResetSlice.reducer; 